import {
  html,
  ifDefined,
  nothing,
  useState,
  virtual,
  useEffect
} from '@arr2019/app-libs';
import { ENDPOINTS, locale, TIME_OUT } from '../../lib/config.js';
import {
  isEventClick,
  isKeyEnter,
  isKeyEnterOrKeySpace
} from 'a11y-key-conjurer';
import { Progress } from '../../ui/progress/progress.js';
import { APP_TITLE } from '../../generated/env.js';
import { formatString } from '@arr2019/format-string';
import { languageSelector } from './modules/language-selector.js';

export const ViewMain = virtual(() => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [qrUrlSvg, setQrUrlSvg] = useState(null);
  const [qrUrlPng, setQrUrlPng] = useState(null);
  const [qrContent, setQrContent] = useState(null);
  const [submitActive, setSubmitActive] = useState(false);
  const [currentLocale, setCurrentLocale] = useState(locale());
  const [showLanguagesList, setShowLanguagesList] = useState(false);
  const [messages, setMessages] = useState({});

  useEffect(async () => {
    const data = await import(`./_i18n/${currentLocale}.json`);
    setMessages(data.default);
  }, [currentLocale]);

  function handleValidation(ev) {
    setSubmitActive(ev.currentTarget.validity.valid);
  }

  function handleReset(ev) {
    if (isEventClick(ev) || isKeyEnterOrKeySpace(ev)) {
      ev.preventDefault();
      setQrUrlSvg(null);
      setQrUrlPng(null);
      setQrContent(null);
      setSubmitActive(false);
    }
  }

  async function handleFetchQr({ format = 'SVG', valueQrContent }) {
    const formData = JSON.stringify({
      qrContent: valueQrContent,
      format
    });
    try {
      const resp = await fetch(ENDPOINTS.BUILD_QR, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: formData
      });

      if (!resp.ok) {
        let msg = `Server error ${resp.status}`;
        const json = await resp.json();
        if (json?.error) {
          msg = json.error;
        }
        setError(msg);
        return false;
      }

      return await resp.blob();
    } catch (err) {
      console.error('QR fetch failed:', err);
      setError('QR fetch failed');
    } finally {
      setIsLoading(false);
    }
  }

  function handleGenerateQr(ev) {
    if (isEventClick(ev) || isKeyEnterOrKeySpace(ev)) {
      ev.preventDefault();
      const valueQrContent = document.querySelector('[name="qrContent"]').value;
      setQrContent(valueQrContent);
      setError(null);
      setIsLoading(true);
      setTimeout(() => {
        handleFetchQr({ format: 'SVG', valueQrContent }).then(blob => {
          if (qrUrlSvg) {
            URL.revokeObjectURL(qrUrlSvg);
          }
          const objUrl = URL.createObjectURL(blob);
          setQrUrlSvg(objUrl);
        });
      }, TIME_OUT);
    }
  }

  function handlePngDownload(ev) {
    if (isEventClick(ev) || isKeyEnter(ev)) {
      ev.preventDefault();
      setIsLoading(true);
      setTimeout(() => {
        handleFetchQr({ format: 'PNG', valueQrContent: qrContent }).then(
          blob => {
            if (qrUrlPng) {
              URL.revokeObjectURL(qrUrlPng);
            }
            const objUrl = URL.createObjectURL(blob);
            setQrUrlPng(objUrl);
            setTimeout(() => {
              document.querySelector('[download="qr.png"]').click();
            }, 100);
          }
        );
      }, TIME_OUT);
    }
  }

  return html`
    <div
      class="flex justify-center items-center p-12 text-neutral-950 dark:text-white"
    >
      <div
        class="w-full min-h-128 max-w-2xl p-8 bg-white dark:bg-neutral-950 rounded-xl shadow flex flex-col"
      >
        <div class="flex justify-center mb-6">
          <h1 class="text-4xl font-header-main">${APP_TITLE}</h1>
          <div class="ml-auto">
            ${languageSelector({
              currentLocale,
              setCurrentLocale,
              showLanguagesList,
              setShowLanguagesList
            })}
          </div>
        </div>
        <div class="space-self-all">
          ${isLoading
            ? Progress({})
            : qrUrlSvg
              ? html`
                  <p class="text-center">${messages.generateSuccess}:</p>
                  <div class="px-12">
                    <img src="${qrUrlSvg}" alt="QR code" class="w-full" />
                  </div>
                  <ul class="flex justify-center mb-6 list-none">
                    <li>
                      <a
                        class="link-text--icon"
                        href="${qrUrlSvg}"
                        download="qr.svg"
                      >
                        <i class="icon icon--download"></i>
                        ${formatString(messages.downloadFormat, [], {
                          format: 'SVG'
                        })}
                      </a>
                    </li>
                    <li class="ml-4">
                      ${qrUrlPng
                        ? html`
                            <a
                              class="link-text--icon"
                              href="${qrUrlPng}"
                              download="qr.png"
                            >
                              <i class="icon icon--download"></i>
                              ${formatString(messages.downloadFormat, [], {
                                format: 'PNG'
                              })}
                            </a>
                          `
                        : html`
                            <span
                              class="link-text--icon"
                              tabindex="0"
                              role="link"
                              @click="${handlePngDownload}"
                              @keydown="${handlePngDownload}"
                            >
                              <i class="icon icon--download"></i>
                              ${formatString(messages.downloadFormat, [], {
                                format: 'PNG'
                              })}
                            </span>
                          `}
                    </li>
                  </ul>
                  <div class="flex justify-center">
                    <span
                      class="submit-button--icon"
                      role="button"
                      tabindex="0"
                      @click="${handleReset}"
                      @keydown="${handleReset}"
                    >
                      <i class="icon icon--qr-code"></i>
                      ${messages.buttonReset}
                    </span>
                  </div>
                `
              : html`
                  ${error ? html`<p>${error}</p>` : nothing}
                  <div class="mb-6">
                    <label for="input-qrCode" class="flex flex-col">
                      <span class="mb-2">${messages.inputTextLabel}</span>
                      <textarea
                        class="input-text"
                        name="qrContent"
                        cols="30"
                        rows="10"
                        maxlength="4000"
                        placeholder="${messages.inputTextPlaceholder}"
                        required="required"
                        @keyup="${handleValidation}"
                        @blur="${handleValidation}"
                        spellcheck="false"
                        id="input-qrCode"
                      ></textarea>
                    </label>
                  </div>
                  <div class="flex justify-center">
                    <span
                      class="submit-button--icon"
                      role="button"
                      tabindex="${submitActive ? '0' : '-1'}"
                      aria-disabled="${ifDefined(
                        submitActive ? undefined : 'true'
                      )}"
                      @click="${ifDefined(
                        submitActive ? handleGenerateQr : undefined
                      )}"
                      @keydown="${ifDefined(
                        submitActive ? handleGenerateQr : undefined
                      )}"
                    >
                      <i class="icon icon--qr-code"></i>
                      ${messages.buttonGenerate}
                    </span>
                  </div>
                `}
        </div>
      </div>
    </div>
  `;
});
